import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  selectInstance,
  setEquipmentVisible,
  setProcessVisible,
} from '../../features/equipment/equipmentSlice';
import { selectTrackNode } from '../../features/layout/layoutSlice';
import { clearSelection, selectElement } from '../../features/selection/selectionSlice';
import {
  setActive3DCameraPreset,
  setShowSimulationTrackNodes,
  setZoomLevel,
} from '../../features/ui/uiSlice';
import type { EquipmentInstance } from '../../features/equipment/equipmentTypes';
import { useAppDispatch, useAppSelector } from '../../hooks/redux';
import { useSimulationTrackNodes } from '../../hooks/useSimulationTrackNodes';
import { viewportStore } from '../../store/viewportStore';
import type { FloorPlanBounds, LayoutElement } from '../../types';
import { equipmentProcessKey, equipmentReviewStatus } from '../../utils/equipmentGeometry';
import { canvasChromeInsets, computeFitViewport } from '../../utils/flatPlanCamera';
import { elementAxisAlignedBounds } from '../../utils/layoutImport';
import { isLayoutEquipmentElement } from '../../utils/layout3d';
import { resolveLayoutElementDisplayName } from '../../utils/layoutElementDisplayName';
import { annotationForTrackNode, trackNodeDisplayName } from '../../utils/trackNodeAnnotations';
import { ALL_RAIL_TYPES } from '../../utils/topology';

type Category = 'ohts' | 'stockers' | 'tracks' | 'nodes' | 'equipment' | 'others';
type CatalogItem =
  | {
      key: string;
      kind: 'layout';
      id: string;
      name: string;
      typeLabel: string;
      element: LayoutElement;
    }
  | {
      key: string;
      kind: 'equipment';
      id: string;
      name: string;
      typeLabel: string;
      instance: EquipmentInstance;
    }
  | {
      key: string;
      kind: 'node';
      id: string;
      name: string;
      typeLabel: string;
      x: number;
      y: number;
    };

const CATEGORIES: Array<{ id: Category; label: string }> = [
  { id: 'ohts', label: 'OHTs' },
  { id: 'stockers', label: 'Stockers' },
  { id: 'tracks', label: 'Tracks' },
  { id: 'nodes', label: 'Nodes' },
  { id: 'equipment', label: 'Equipment' },
  { id: 'others', label: 'Others' },
];

const MIN_FOCUS_SPAN = 80;

function layoutCategory(element: LayoutElement): Category {
  if (element.type === 'oht-vehicle') return 'ohts';
  if (ALL_RAIL_TYPES.has(element.type)) return 'tracks';
  if (element.type === 'stocker' || element.customProperties?.objectType === 'stocker') {
    return 'stockers';
  }
  if (isLayoutEquipmentElement(element)) return 'equipment';
  return 'others';
}

function paddedFocusBounds(bounds: FloorPlanBounds): FloorPlanBounds {
  const centerX = (bounds.minX + bounds.maxX) / 2;
  const centerY = (bounds.minY + bounds.maxY) / 2;
  const width = Math.max(bounds.maxX - bounds.minX, MIN_FOCUS_SPAN);
  const height = Math.max(bounds.maxY - bounds.minY, MIN_FOCUS_SPAN);
  return {
    minX: centerX - width / 2,
    maxX: centerX + width / 2,
    minY: centerY - height / 2,
    maxY: centerY + height / 2,
  };
}

function polygonBounds(instance: EquipmentInstance): FloorPlanBounds | null {
  if (instance.polygon.length === 0) return null;
  const xs = instance.polygon.map((point) => point.x);
  const ys = instance.polygon.map((point) => point.y);
  return {
    minX: Math.min(...xs),
    maxX: Math.max(...xs),
    minY: Math.min(...ys),
    maxY: Math.max(...ys),
  };
}

const ElementsPanel: React.FC = () => {
  const dispatch = useAppDispatch();
  const elements = useAppSelector((state) => state.layout.elements);
  const elementOrder = useAppSelector((state) => state.layout.elementOrder);
  const annotations = useAppSelector((state) => state.layout.trackNodeAnnotations);
  const equipmentInstances = useAppSelector((state) => state.equipment.instances);
  const equipmentOrder = useAppSelector((state) => state.equipment.instanceOrder);
  const leftSidebarOpen = useAppSelector((state) => state.ui.leftSidebarOpen);
  const rightSidebarOpen = useAppSelector((state) => state.ui.rightSidebarOpen);
  const view3dOpen = useAppSelector((state) => state.ui.view3dOpen);
  const trackNodes = useSimulationTrackNodes();
  const [activeCategory, setActiveCategory] = useState<Category>('ohts');
  const [query, setQuery] = useState('');
  const [focusedKey, setFocusedKey] = useState<string | null>(null);
  const focusedKeyRef = useRef<string | null>(null);

  const clearFocusedItem = useCallback(() => {
    if (!focusedKeyRef.current) return;
    focusedKeyRef.current = null;
    setFocusedKey(null);
    dispatch(clearSelection());
    dispatch(selectInstance(null));
    dispatch(selectTrackNode(null));
  }, [dispatch]);

  useEffect(() => {
    const clearOnPointerDown = (event: PointerEvent) => {
      const row = (event.target as HTMLElement | null)?.closest<HTMLElement>('[data-element-key]');
      if (row?.dataset.elementKey === focusedKeyRef.current) return;
      clearFocusedItem();
    };
    const clearOnAction = () => clearFocusedItem();
    document.addEventListener('pointerdown', clearOnPointerDown, true);
    document.addEventListener('keydown', clearOnAction, true);
    document.addEventListener('wheel', clearOnAction, { capture: true, passive: true });
    return () => {
      document.removeEventListener('pointerdown', clearOnPointerDown, true);
      document.removeEventListener('keydown', clearOnAction, true);
      document.removeEventListener('wheel', clearOnAction, true);
      clearFocusedItem();
    };
  }, [clearFocusedItem]);

  const itemsByCategory = useMemo(() => {
    const result: Record<Category, CatalogItem[]> = {
      ohts: [],
      stockers: [],
      tracks: [],
      nodes: [],
      equipment: [],
      others: [],
    };
    const linkedEquipmentIds = new Set<string>();

    elementOrder.forEach((id) => {
      const element = elements[id];
      if (!element) return;
      const linkedId = element.customProperties?.dxfEquipmentId;
      const linkedInstance =
        typeof linkedId === 'string' ? equipmentInstances[linkedId] : undefined;
      if (linkedInstance) linkedEquipmentIds.add(linkedInstance.id);
      result[layoutCategory(element)].push({
        key: `layout:${element.id}`,
        kind: 'layout',
        id: element.id,
        name:
          linkedInstance?.name || resolveLayoutElementDisplayName(element, elements, elementOrder),
        typeLabel: element.type,
        element,
      });
    });

    equipmentOrder.forEach((id) => {
      const instance = equipmentInstances[id];
      if (
        !instance ||
        linkedEquipmentIds.has(id) ||
        equipmentReviewStatus(instance) === 'rejected'
      ) {
        return;
      }
      const category: Category = instance.objectType === 'stocker' ? 'stockers' : 'equipment';
      result[category].push({
        key: `equipment:${instance.id}`,
        kind: 'equipment',
        id: instance.id,
        name: instance.name || instance.id,
        typeLabel: instance.objectType,
        instance,
      });
    });

    trackNodes.forEach((node) => {
      const annotation = annotationForTrackNode(annotations, node);
      result.nodes.push({
        key: `node:${node.nodeId}`,
        kind: 'node',
        id: node.nodeId,
        name: trackNodeDisplayName(annotation, node.nodeId),
        typeLabel: annotation.role === 'none' ? 'track node' : annotation.role,
        x: node.x,
        y: node.y,
      });
    });

    return result;
  }, [annotations, elementOrder, elements, equipmentInstances, equipmentOrder, trackNodes]);

  const visibleItems = useMemo(() => {
    const normalizedQuery = query.trim().toLocaleLowerCase();
    if (!normalizedQuery) return itemsByCategory[activeCategory];
    return itemsByCategory[activeCategory].filter(
      (item) =>
        item.id.toLocaleLowerCase().includes(normalizedQuery) ||
        item.name.toLocaleLowerCase().includes(normalizedQuery)
    );
  }, [activeCategory, itemsByCategory, query]);

  const focus2d = (bounds: FloorPlanBounds) => {
    const viewport = viewportStore.getState();
    const fit = computeFitViewport(
      paddedFocusBounds(bounds),
      viewport.canvasWidth || 1200,
      viewport.canvasHeight || 800,
      0.72,
      canvasChromeInsets({ leftSidebarOpen, rightSidebarOpen })
    );
    viewportStore.setState(fit);
    dispatch(setZoomLevel(fit.zoom));
  };

  const selectItem = (item: CatalogItem) => {
    if (focusedKeyRef.current === item.key) {
      clearFocusedItem();
      return;
    }

    dispatch(clearSelection());
    dispatch(selectInstance(null));
    dispatch(selectTrackNode(null));

    if (item.kind === 'layout') {
      dispatch(selectElement(item.id));
      if (view3dOpen) {
        dispatch(setActive3DCameraPreset('focus'));
      } else {
        focus2d(elementAxisAlignedBounds(item.element));
      }
    } else if (item.kind === 'equipment') {
      dispatch(setEquipmentVisible(true));
      dispatch(setProcessVisible({ process: equipmentProcessKey(item.instance), visible: true }));
      dispatch(selectInstance(item.id));
      const bounds = polygonBounds(item.instance);
      if (bounds && !view3dOpen) focus2d(bounds);
    } else {
      dispatch(setShowSimulationTrackNodes(true));
      dispatch(selectTrackNode(item.id));
      if (!view3dOpen) {
        focus2d({ minX: item.x, maxX: item.x, minY: item.y, maxY: item.y });
      }
    }

    focusedKeyRef.current = item.key;
    setFocusedKey(item.key);
  };

  return (
    <section className="flex min-h-full flex-col" aria-label="Elements">
      <div className="sticky top-0 z-10 border-b border-slate-200 bg-white px-3 pb-3 pt-4">
        <h2 className="mb-3 text-sm font-semibold text-slate-800">Elements</h2>
        <label className="relative block">
          <SearchIcon />
          <input
            type="search"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder="Search by ID or name"
            className="w-full rounded-lg border border-slate-200 bg-slate-50 py-2 pl-9 pr-3 text-xs text-slate-700 outline-none transition focus:border-primary-400 focus:bg-white focus:ring-2 focus:ring-primary-100"
          />
        </label>
        <div className="mt-3 grid grid-cols-3 gap-1" role="tablist" aria-label="Element groups">
          {CATEGORIES.map((category) => (
            <button
              key={category.id}
              type="button"
              role="tab"
              aria-selected={activeCategory === category.id}
              onClick={() => setActiveCategory(category.id)}
              className={`rounded-md px-2 py-1.5 text-[11px] font-medium transition-colors ${
                activeCategory === category.id
                  ? 'bg-primary-50 text-primary-700'
                  : 'text-slate-500 hover:bg-slate-100 hover:text-slate-700'
              }`}
            >
              {category.label}
              <span className="ml-1 text-[10px] opacity-60">
                {itemsByCategory[category.id].length}
              </span>
            </button>
          ))}
        </div>
      </div>

      <div className="flex-1 px-2 py-2">
        {visibleItems.length === 0 ? (
          <div className="px-3 py-10 text-center text-xs text-slate-400">
            {query.trim() ? 'No matching elements' : 'No elements in this group'}
          </div>
        ) : (
          <ul className="space-y-1">
            {visibleItems.map((item) => {
              const focused = focusedKey === item.key;
              return (
                <li key={item.key}>
                  <button
                    type="button"
                    data-element-key={item.key}
                    onClick={() => selectItem(item)}
                    title={`Focus ${item.name}`}
                    className={`flex w-full items-center gap-2 rounded-lg border px-2.5 py-2 text-left transition-colors ${
                      focused
                        ? 'border-primary-300 bg-primary-50'
                        : 'border-transparent hover:border-slate-200 hover:bg-slate-50'
                    }`}
                  >
                    <ElementTypeIcon active={focused} />
                    <span className="min-w-0 flex-1">
                      <span
                        className={`block truncate text-xs font-medium ${
                          focused ? 'text-primary-700' : 'text-slate-700'
                        }`}
                      >
                        {item.name}
                      </span>
                      <span className="mt-0.5 block truncate text-[10px] text-slate-400">
                        {item.id} · {item.typeLabel}
                      </span>
                    </span>
                    <FocusIcon />
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </section>
  );
};

const SearchIcon: React.FC = () => (
  <svg
    className="pointer-events-none absolute left-3 top-2.5 h-4 w-4 text-slate-400"
    viewBox="0 0 16 16"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.5"
  >
    <circle cx="7" cy="7" r="4" />
    <path d="m10 10 3 3" strokeLinecap="round" />
  </svg>
);

const ElementTypeIcon: React.FC<{ active: boolean }> = ({ active }) => (
  <span
    className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-md ${
      active ? 'bg-primary-100 text-primary-600' : 'bg-slate-100 text-slate-400'
    }`}
  >
    <svg className="h-4 w-4" viewBox="0 0 16 16" fill="none" stroke="currentColor">
      <rect x="3" y="3" width="10" height="10" rx="2" strokeWidth="1.4" />
      <circle cx="8" cy="8" r="1.5" fill="currentColor" stroke="none" />
    </svg>
  </span>
);

const FocusIcon: React.FC = () => (
  <svg
    className="h-3.5 w-3.5 shrink-0 text-slate-300"
    viewBox="0 0 16 16"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.4"
  >
    <path d="M6 3H3v3M10 3h3v3M6 13H3v-3M10 13h3v-3" strokeLinecap="round" />
  </svg>
);

export default ElementsPanel;
